import { CheckIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import Button from '../../components/Button'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import twitterLogo from '../../assets/twitter-62x62.png'
import facebookLogo from '../../assets/facebook-62x62.png'
import youtubeLogo from '../../assets/youtube_logo_62x43.png'
import instagramLogo from '../../assets/instagram-62x62.png'
import tiktokLogo from '../../assets/tiktok_logo_65x65.jpg'
import { ProgressBar } from '../../components/guided-onboarding/ProgressBar'
import { useRecoilState } from 'recoil'
import { guidedOnboardingIncompleteState } from '../../store/DomainStore'
import { toastSuccess } from '../../components/Notification'

export default function ConnectStepPage() {
    const platforms = [
        {
            title: 'Instagram profile',
            to: '/account/new/instagram_direct',
            icon: instagramLogo,
            serviceId: 2,
            bgColor: 'bg-pink-500/10'
        },
        {
            title: 'Facebook page',
            to: '/account/new/facebook_page',
            icon: facebookLogo,
            serviceId: 3,
            bgColor: 'bg-blue-500/10'
        },
        {
            title: 'X profile',
            to: '/account/new/twitter',
            icon: twitterLogo,
            serviceId: 1,
            bgColor: 'bg-sky-500/10'
        },
        {
            title: 'YouTube channel',
            to: '/account/new/youtube',
            icon: youtubeLogo,
            serviceId: 4,
            bgColor: 'bg-red-500/10'
        },
        {
            title: 'TikTok Paid Account',
            to: '/account/new/tiktok_ad',
            icon: tiktokLogo,
            serviceId: 6,
            bgColor: 'bg-cyan-500/10'
        },
        {
            title: 'TikTok Organic Account',
            to: '/account/new/tiktok_user',
            icon: tiktokLogo,
            serviceId: 7,
            bgColor: 'bg-cyan-500/10'
        }
    ]

    const [showProgressBar, setShowProgressBar] = useRecoilState(guidedOnboardingIncompleteState)

    const navigate = useNavigate()

    const { state }: any = useLocation()

    let numberOfNewAccounts = 0
    if (state?.newAccountAuthorised) {
        numberOfNewAccounts = state.authorisedAccounts?.length || 1
    }

    const { connectedAccounts } = useOutletContext<{ connectedAccounts: Map<number, number> }>()

    const hasConnectedAccounts = !!connectedAccounts && connectedAccounts.size > 0

    useEffect(() => {
        // If connectedAccounts is undefined, it hasn't loaded yet.
        if (!!connectedAccounts) {
            if (numberOfNewAccounts > 0) {
                // If same number of connected accounts as new accounts, they have just been added and onboarding is in progress
                setShowProgressBar(connectedAccounts.size === numberOfNewAccounts)
            } else {
                // If this isn't a redirect after account success, onboarding must be complete if accounts are present
                setShowProgressBar(!hasConnectedAccounts)
            }
        }
    }, [connectedAccounts, setShowProgressBar, hasConnectedAccounts, numberOfNewAccounts])

    // Ref only neccassary to stop effect running twice during development
    const hasShownToast = useRef(false)
    useEffect(() => {
        if (numberOfNewAccounts > 0 && !hasShownToast.current) {
            hasShownToast.current = true
            toastSuccess('Account connected successfully')
        }
    }, [numberOfNewAccounts])

    const [showConnectionWarning, setShowConnectionWarning] = useState(false)

    const handleContinue = () => {
        if (!connectedAccounts) return
        if (hasConnectedAccounts) {
            navigate('/confirmation')
        } else {
            setShowConnectionWarning(true)
        }
    }

    return (
        <>
            {showProgressBar && (
                <div className="max-w-xl mx-auto">
                    <ProgressBar currentStep={2} totalSteps={3} />
                </div>
            )}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-border shadow-lg max-w-xl mx-auto animate-fade-in">
                <div className="mx-auto h-14 w-14 rounded-full bg-red-50 mb-4 flex items-center justify-center">
                    <ShieldCheckIcon className="w-8 h-8 text-red-500" />
                </div>
                <div className="mb-6 text-center">
                    <h1 className="text-2xl md:text-3xl mb-3">
                        Connect your social media profiles
                    </h1>
                    <p className="text-base text-muted-foreground max-w-2xl mx-auto mb-4">
                        Choose which social media profiles you want to connect
                    </p>
                </div>
                <div className="p-4 bg-primary/5 border border-primary/50 rounded-lg mb-6">
                    <div className="flex items-start gap-3 mb-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <ShieldCheckIcon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <p className="font-medium text-foreground text-sm mb-1">
                                Quick and secure
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Takes 20 seconds per platform. You can remove access any time.
                            </p>
                        </div>
                    </div>

                    <div className="pt-3 border-t border-primary/10">
                        <p className="font-medium text-foreground mb-2 text-xs">
                            What you need before continuing:
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-1.5">
                            <li className="flex items-start gap-2">
                                <span className="text-primary mt-0.5 flex-shrink-0">1.</span>
                                <span>
                                    When you click Connect you'll be redirected to the social
                                    network's authorization page.
                                </span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-primary mt-0.5 flex-shrink-0">2.</span>
                                <span>
                                    Follow the steps to complete authorization and you'll be brought
                                    back to this page.
                                </span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-primary mt-0.5 flex-shrink-0">3.</span>
                                <span>
                                    You may be prompted to log in, so have your credentials
                                    (username and password) for each social media profile to hand.
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>

                <h3 className="text-lg font-semibold text-foreground ">
                    Connect your social media profiles
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                    Choose which social media profiles you want to connect
                </p>

                <div className="grid gap-3 mb-6">
                    {platforms.map((platform) => {
                        const connectedCount = connectedAccounts?.get(platform.serviceId)
                        return (
                            <div
                                key={platform.title}
                                className={`p-4 rounded-lg border transition-all ${
                                    !!connectedCount
                                        ? 'border-lime-400 bg-lime-400/5'
                                        : 'border-border bg-card'
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`w-14 h-14 rounded-lg flex items-center justify-center ${platform.bgColor}`}
                                        >
                                            <img
                                                src={platform.icon}
                                                alt={platform.title}
                                                className="w-10 h-10 object-contain"
                                            />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-foreground">
                                                {platform.title}
                                            </p>
                                            {!!connectedCount && (
                                                <p className="text-sm text-success flex items-center gap-1 mt-0.5 text-lime-500">
                                                    <CheckIcon className="w-3 h-3" />
                                                    {connectedCount} connected
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <Button
                                        onClick={() => navigate(platform.to)}
                                        size="small"
                                        text="Connect"
                                        primary
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
                {!hasConnectedAccounts && showConnectionWarning && (
                    <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg mb-6">
                        <p className="font-medium text-foreground text-sm mb-1">
                            Connect an account to continue
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Connect an account to enable protection before continuing
                        </p>
                    </div>
                )}
                <Button onClick={handleContinue} full text="Continue" primary />
            </div>
        </>
    )
}
